import React from 'react';
import { Code, Copy } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';

export default function ApiDocsPage() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <h1 className="text-3xl font-extrabold text-gray-900">
            API Documentation
          </h1>
          <p className="mt-4 text-lg text-gray-500">
            Integrate 4D EMR into your applications with our REST API.
          </p>

          <div className="mt-8 space-y-8">
            {/* Authentication */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Authentication
              </h2>
              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  All API requests require authentication using an API key in
                  the header:
                </p>
                <div className="bg-gray-800 rounded-md p-4 flex justify-between items-center">
                  <code className="text-sm text-white">
                    Authorization: Bearer YOUR_API_KEY
                  </code>
                  <button
                    onClick={() =>
                      copyToClipboard('Authorization: Bearer YOUR_API_KEY')
                    }
                    className="text-gray-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            </section>

            {/* Endpoints */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Endpoints
              </h2>

              {/* Referrals */}
              <Card className="p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Patients
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 rounded">
                        GET
                      </span>
                      <code className="text-sm">/api/v1/patients</code>
                    </div>
                    <p className="text-sm text-gray-600">List all Patients</p>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-800 rounded">
                        POST
                      </span>
                      <code className="text-sm">/api/v1/patients</code>
                    </div>
                    <p className="text-sm text-gray-600">
                      Create a new Patient
                    </p>
                  </div>
                </div>
              </Card>

              {/* Pipeline Stages */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Appointments
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 rounded">
                        GET
                      </span>
                      <code className="text-sm">/api/v1/appointments</code>
                    </div>
                    <p className="text-sm text-gray-600">
                      List all appointments
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 text-xs font-bold bg-yellow-100 text-yellow-800 rounded">
                        PUT
                      </span>
                      <code className="text-sm">/api/v1/polling/:id/rule</code>
                    </div>
                    <p className="text-sm text-gray-600">Update Rules</p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Rate Limits */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Rate Limits
              </h2>
              <Card className="p-6">
                <p className="text-sm text-gray-600">
                  API requests are limited to:
                </p>
                <ul className="list-disc list-inside mt-2 text-sm text-gray-600">
                  <li>100 requests per minute for standard plans</li>
                  <li>1000 requests per minute for enterprise plans</li>
                </ul>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
